import type { JurisdictionProfile, MathConfig, Strings } from '../types';
import type { Game } from '../engine/game';
import { t } from '../util/format';
import { el } from './modal';

/** Debug panel (?debug=1): force outcomes, set balance, buy feature, switch theme. */
export class DebugPanel {
  private readonly panel: HTMLDivElement;
  private readonly state: HTMLDivElement;
  private readonly unsubscribe: Array<() => void> = [];

  constructor(o: { root: HTMLElement; game: Game; math: MathConfig; jurisdiction: JurisdictionProfile; strings: Strings }) {
    const { root, game, math, jurisdiction, strings } = o;
    this.panel = el('div', 'slot-debug is-collapsed');
    const head = el('div', 'slot-debug__head', t(strings, 'debug'));
    head.appendChild(el('span', undefined, '▾'));
    head.addEventListener('click', () => this.panel.classList.toggle('is-collapsed'));
    const body = el('div', 'slot-debug__body');

    const row = (label?: string) => {
      const r = el('div', 'slot-debug__row');
      if (label) r.appendChild(el('label', undefined, label));
      body.appendChild(r);
      return r;
    };
    const btn = (parent: HTMLElement, label: string, onClick: () => void) => {
      const b = el('button', 'slot-debug__btn', label);
      b.type = 'button';
      b.addEventListener('click', () => {
        onClick();
        this.flash(b);
      });
      parent.appendChild(b);
      return b;
    };

    if (math.scatter) {
      const scatters = row();
      for (const n of Object.keys(math.scatter.freeSpins).map(Number)) btn(scatters, `${n}×S`, () => game.forceNext({ scatterCount: n }));
    }
    const tiers = row();
    for (const tier of ['big', 'mega', 'epic'] as const) btn(tiers, tier.toUpperCase(), () => game.forceNext({ tier }));
    const clear = row();
    btn(clear, 'clear forced', () => game.forceNext(null));

    const bal = row();
    const input = el('input');
    input.type = 'number';
    input.min = '0';
    input.step = '10';
    input.placeholder = t(strings, 'balance');
    bal.appendChild(input);
    btn(bal, 'set', () => {
      const v = Number(input.value);
      if (Number.isFinite(v)) game.setBalance(v);
    });

    if (math.scatter && math.bonusBuy.enabled && jurisdiction.allowBonusBuy) {
      const buy = row();
      btn(buy, `${t(strings, 'buyFeature')} (${math.bonusBuy.costMultiplier}×)`, () => void game.buyFeature().catch(() => {}));
    }

    const theme = row();
    const themeInput = el('input');
    themeInput.type = 'text';
    themeInput.placeholder = 'theme';
    try {
      themeInput.value = new URL(location.href).searchParams.get('theme') ?? 'lucky-fruit';
    } catch {
      themeInput.value = 'lucky-fruit';
    }
    theme.appendChild(themeInput);
    btn(theme, 'load', () => {
      const url = new URL(location.href);
      url.searchParams.set('theme', themeInput.value.trim() || 'lucky-fruit');
      location.href = url.href;
    });

    const brand = row();
    const brandInput = el('input');
    brandInput.type = 'text';
    brandInput.placeholder = 'brand (empty = none)';
    try {
      brandInput.value = new URL(location.href).searchParams.get('brand') ?? '';
    } catch {
      brandInput.value = '';
    }
    brand.appendChild(brandInput);
    btn(brand, 'load', () => {
      const url = new URL(location.href);
      const v = brandInput.value.trim();
      if (v) url.searchParams.set('brand', v);
      else url.searchParams.delete('brand');
      location.href = url.href;
    });

    this.state = el('div', 'slot-debug__state');
    body.appendChild(this.state);
    this.panel.append(head, body);
    root.appendChild(this.panel);

    this.unsubscribe.push(
      game.on('snapshot', (s) => {
        this.state.textContent = `${s.state} · ${s.mode}${s.freeSpins ? ` ${s.freeSpins.remaining}/${s.freeSpins.total}` : ''} · win ${s.lastWin}`;
      }),
    );
  }

  private flash(b: HTMLButtonElement): void {
    b.classList.add('is-active');
    setTimeout(() => b.classList.remove('is-active'), 300);
  }

  destroy(): void {
    for (const u of this.unsubscribe) u();
    this.panel.remove();
  }
}
